/**
 * How this child likes to play — jump, point, or tap.
 *
 * 🔴 A CHOICE, NOT A FALLBACK. THAT IS THE ENTIRE POINT OF THIS FILE.
 *
 * The tap path already existed and already worked — invariant I3 routes it
 * through the same machine as the camera, so nothing about it is second-class
 * in code. But it was *presented* as a rescue: the round offered
 * *"Having trouble? Tap instead"* after three failed attempts, which means the
 * only child ever told about it was a child already having a bad time, and the
 * offer itself named them as struggling.
 *
 * A child who simply prefers to tap — or whose classroom is too cramped to
 * jump in, or who uses a wheelchair, or who is shy in front of a camera on
 * their first day — had to fail three times to be told the thing they wanted
 * was available. So the preference is asked for up front, kept, and changeable
 * at any time.
 *
 * 🔴 IT DOES NOT MAKE THE GAME EASIER OR HARDER, AND MUST NOT. `calculateReward`
 * sees only `{ correct, total, level }`, and `check-game-contract.mjs` keeps a
 * game from telling a jump from a key press. This module stores a PRESENTATION
 * preference; the assessment is identical down every path, which is what makes
 * offering the choice honest rather than a difficulty setting in disguise.
 *
 * ⚠️ EXCEPT FOR ONE THING, AND IT IS RECORDED RATHER THAN HIDDEN: a tap earns
 * no `GESTURE_JUMP` event, because nothing physical happened (OD-32,
 * `events.ts`). A child who plays entirely by tapping will show numeracy
 * progress and no physical-development progress — which is TRUE, and is the
 * report a teacher should see. It is not a penalty; it is the absence of a
 * claim nobody can make.
 */

/** The three ways to answer. Both camera modes read the same signals ref. */
export type PlayMode =
  /** Whole-body: a jump places a frog. The gross-motor core (games-spec §1). */
  | 'jump'
  /** Upper-body: a raised wrist places a frog, and points at choices. */
  | 'point'
  /** Touch or mouse. No camera required at all. */
  | 'tap'

export const PLAY_MODES: readonly PlayMode[] = ['jump', 'point', 'tap'] as const

const STORAGE_KEY = 'kalqy.playMode'

/**
 * Minimal storage, injectable — the same shape and the same reason as
 * `device.ts`: the tests run under Node with no DOM, and Safari private mode
 * throws on ACCESS rather than on use.
 */
export interface PreferenceStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

function defaultStorage(): PreferenceStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function isPlayMode(v: unknown): v is PlayMode {
  return v === 'jump' || v === 'point' || v === 'tap'
}

/**
 * The stored preference, or null when the child has not chosen.
 *
 * 🔴 NULL IS A REAL STATE AND MUST NOT BE COLLAPSED TO A DEFAULT HERE. "Has not
 * chosen yet" is what makes the chooser appear; defaulting to `'jump'` in the
 * reader would mean a first-time child is silently assigned the most physically
 * demanding mode and never asked.
 */
export function getPlayMode(storage: PreferenceStorage | null = defaultStorage()): PlayMode | null {
  try {
    const raw = storage?.getItem(STORAGE_KEY)
    return isPlayMode(raw) ? raw : null
  } catch {
    return null
  }
}

export function setPlayMode(mode: PlayMode, storage: PreferenceStorage | null = defaultStorage()): void {
  try {
    storage?.setItem(STORAGE_KEY, mode)
  } catch {
    // Non-fatal. The child plays this session in the mode they picked; only the
    // memory of it is lost, and being asked again is a small cost next to a
    // boot that fails because storage is disabled.
  }
}

/**
 * What the vision kit should be told, given a play mode.
 *
 * 🔴 `jump` AND `point` ARE BOTH `camera` TO THE KIT. The kit's `InputMode` is
 * about whether a camera is running at all; the difference between jumping and
 * pointing is which signal the child prefers to produce, and BOTH are already
 * live on the same ref simultaneously. Collapsing them here means the kit never
 * learns a distinction it has no use for — and a child who chose "point" but
 * jumps anyway still places a frog, which is the correct and forgiving
 * behaviour.
 */
export function kitInputMode(mode: PlayMode): 'camera' | 'tap' {
  return mode === 'tap' ? 'tap' : 'camera'
}

/**
 * Whether a mode implies the seated posture.
 *
 * `point` maps to seated because `fallback/seated.ts` substitutes an arm-raise
 * for a jump — which is exactly what a child who chose "point" is offering.
 */
export function postureFor(mode: PlayMode): 'standing' | 'seated' {
  return mode === 'jump' ? 'standing' : 'seated'
}
